/**
 * Trang Kiểm tra phí dịch vụ (PDV)
 * ─────────────────────────────────────────────────────────────────────────────
 * Quy trình:
 *  1. Upload file Excel "Bảng tách PDV tháng X"
 *  2. FE tự detect 2 sheet:
 *       • Sheet PDV chính (vd: "PDV 12.2025")
 *         → đọc thông tin chung, per-employee NC/commission, aggregate totals
 *         → 1 căn có thể có 2 dòng Excel (sale1+ctv1 trên row1, sale2+ctv2 trên row2)
 *           → TÍCH LŨY (không dedup) để lấy đủ 4 nhân viên
 *       • Sheet "PDV theo căn"
 *         → đọc cột MNV, Mã căn, và các trường tiền (PDV, CC/KQ, KT, sau KT, thuế, sau thuế)
 *  3. Gọi BE POST /commissionAnalyst/checkPDVExcel so sánh với DB
 *  4. Hiển thị MATCH ✅ / MISMATCH ❌ / ERROR ⚠️
 *
 * Mapping cột "PDV chính" (theo thứ tự cột trong Excel):
 *   Hệ số dự án (K)                 → kRate
 *   NCC                             → standardWorkingDaysInMonth
 *   Giá tính PDV                    → priceForCommission
 *   Tỷ lệ nhận về                   → transactionRate
 *   Tỷ lệ TN                        → incomeRate
 *   Mã Sale 1                       → sale1/sale2 employee code (dòng 1/dòng 2)
 *   NC [0]                          → NC của sale1, [1] = sale2, [2..4] = GĐK DH/DT/CL, [5..7] = GĐV DH/DT/CL, [8] = Người GTDL
 *   Số tiền TN [0..1]               → incomePrice của sale1, sale2
 *   Số tiền PDV CVKD [0..1]          → salesCommissionPrice của sale1, sale2
 *   Số tiền PDV CVKD Elite [0..1]    → eliteSalesCommissionPrice của sale1, sale2
 *   Mã [0..7]                       → GĐK DH/DT/CL, GĐV DH/DT/CL, Người GTDL, TGĐ
 *   Tỷ lệ PDV [3]                    → Tổng GĐK rate, [7] = Tổng GĐV rate, [9] = Tổng cộng rate
 *   Số tiền Tổng GĐK/GĐV            → aggregate price của từng block
 *   Tổng cộng PDV Số tiền            → totalCommissionPrice
 *
 * Mapping cột "PDV theo căn":
 *   MNV                              → codeEmployee
 *   Họ và tên (cột ngay sau MNV)    → fullName
 *   Mã căn    (MNV+4, hoặc search)  → codeApartment
 *   Tổng cộng PDV                   → totalPDV
 *   Chứng chỉ/ ký quỹ               → certificateOrDeposit
 *   KT do đã ĐC kỳ trước/...        → accounting
 *   PDV còn lại sau KT              → remainingAfterAccounting
 *   Thuế TNCN                       → personalIncomeTax
 *   PDV còn lại sau thuế            → remainingAfterTax
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Select,
  Text,
  Badge,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  VStack,
} from "@chakra-ui/react";
import {
  IoArrowBackOutline,
  IoChevronDownOutline,
  IoChevronUpOutline,
} from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { commissionAnalystCall } from "../../api/commissionAnalyst";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const trim = (v) =>
  String(v ?? "")
    .trim()
    .normalize("NFC");
const toNum = (v) => {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return v;
  return Number(String(v).replace(/[^0-9.\-]/g, "")) || 0;
};

/**
 * XLSX đọc ô định dạng % trong Excel thành dạng thập phân:
 *   50%  → 0.5    100% → 1.0    0.955% → 0.00955
 * DB lưu dạng nguyên: 50, 100, 0.955
 * → Nếu giá trị 0 < v ≤ 1 thì nhân 100 để đưa về đúng format DB
 * → Giữ 4 chữ số thập phân để không mất độ chính xác với % nhỏ (vd: 0.955%)
 */
const normPercent = (v) => {
  const n = toNum(v);
  return n > 0 && n <= 1 ? Math.round(n * 1000000) / 10000 : n;
};

const isRateLabel = (label) =>
  label.includes("%") || label.toLowerCase().includes("tỷ lệ");

/** Các label hiển thị dạng số thuần (không có đơn vị tiền và không làm tròn) */
const isPlainNumberLabel = (label) => {
  const l = label.toLowerCase();
  return (
    l.includes("hệ số") ||
    l.includes("ngày công chuẩn") ||
    l.startsWith("nc (") ||
    l === "ncc"
  );
};

const fmtValue = (label, v) => {
  if (v == null) return "-";
  if (isRateLabel(label)) return `${v}%`;
  if (isPlainNumberLabel(label)) return String(Number(v)); // plain number, no unit
  return Math.round(Number(v)).toLocaleString("vi-VN") + " ₫";
};

// Kiểm tra cell có phải dòng cần bỏ qua không
// Các trường hợp skip:
//   • rỗng / null
//   • dòng TỔNG CỘNG
//   • số tham chiếu cột dạng "(2)", "(10)"
//   • số nhỏ ≤ 3 chữ số (STT, hoặc ô trống format số mà XLSX trả về 0/1/2...)
// ⚠️ KHÔNG dùng /^\(?\d+\)?$/ (optional-parens) vì sẽ skip nhầm mã căn thuần số
//    4+ chữ số như "0702", "2303" — regex dưới chỉ skip số ≤ 3 chữ số không có chữ cái
const isSkipRow = (val) => {
  const s = trim(val);
  return (
    !s ||
    /^TỔNG/i.test(s) || // dòng tổng cộng
    /^\(\d+\)$/.test(s) || // tham chiếu cột: (2), (10), ...
    /^\d{1,3}$/.test(s) // số thuần ≤ 3 chữ số: 0, 1, 2, ... 999
  );
};

// ─── Parse sheet PDV chính (PDV 12.2025 / PDV 01.2026) ───────────────────────
/**
 * Trả về:
 *  maCanToTyLe    : Map<maCan, number[]>   – mỗi maCan có thể nhiều Tỷ lệ ĐC
 *  empMaCanToTyLe : Map<maCan_empCode, number> – khóa kép để tra chính xác
 *  mainTabRows    : Array<{codeApartment, calculateCommissionRate, ...}>
 *                   – mỗi căn × đợt (ĐÃ TÍCH LŨY từ nhiều dòng Excel cùng maCan/tyLe)
 *                   – gồm: thông tin chung, per-employee arrays, aggregate totals
 *
 * Cấu trúc cột NC (sorted by column position):
 *   ncCols[0]  = NC sale1 (dòng 1 của căn)
 *   ncCols[1]  = NC sale2 (CTV của sale1, cùng dòng)
 *   ncCols[2]  = NC GĐK ĐH
 *   ncCols[3]  = NC GĐK ĐT
 *   ncCols[4]  = NC GĐK CL
 *   ncCols[5]  = NC GĐV ĐH
 *   ncCols[6]  = NC GĐV ĐT
 *   ncCols[7]  = NC GĐV CL
 *   ncCols[8]  = NC Người GTDL
 *   (TGĐ không có NC)
 *
 * Cấu trúc cột "Mã" thuần (maCols, sorted):
 *   maCols[0]  = GĐK ĐH
 *   maCols[1]  = GĐK ĐT
 *   maCols[2]  = GĐK CL
 *   maCols[3]  = GĐV ĐH
 *   maCols[4]  = GĐV ĐT
 *   maCols[5]  = GĐV CL
 *   maCols[6]  = Người GTDL
 *   maCols[7]  = TGĐ (Giám đốc)
 *
 * Cấu trúc "Tỷ lệ PDV" (tyLePDVCols, sorted):
 *   [0]=GDKĐH, [1]=GDKĐT, [2]=GDKCL, [3]=Tổng GĐK
 *   [4]=GDVĐH, [5]=GDVĐT, [6]=GDVCL, [7]=Tổng GĐV
 *   [8]=TGĐ,   [9]=Tổng cộng
 */
const parsePDVMainTab = (wb, sheetName) => {
  const result = {
    maCanToTyLe: new Map(),
    empMaCanToTyLe: new Map(),
    mainTabRows: [],
  };
  const ws = wb.Sheets[sheetName];
  if (!ws) return result;

  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });

  // ══════════════════════════════════════════════════════════════════════════
  // Lý do dùng 2-pass:
  //   Excel thường có vùng tiêu đề/mô tả ở các dòng đầu (rows 0-5).
  //   Vùng đó có thể chứa chữ "Giá tính PDV", "NCC"... nhưng ở vị trí CỘT
  //   KHÁC với cột dữ liệu thực. Nếu scan tất cả 0-20 dòng, code sẽ lấy
  //   nhầm vị trí từ vùng tiêu đề đó → đọc dữ liệu ra toàn 0 hoặc sai.
  //   Fix: pass1 tìm hRow (dòng có "Mã lô"+"Tỷ lệ ĐC"), pass2 chỉ quét
  //   trong phạm vi hRow±2 để đảm bảo đúng cột, dùng exact matching (NFC).
  // ══════════════════════════════════════════════════════════════════════════

  // ── Pass 1: Tìm hRow ─────────────────────────────────────────────────────
  let hRow = -1,
    maLoIdx = -1,
    tyLeIdx = -1;
  for (let i = 0; i < Math.min(raw.length, 30); i++) {
    const row = raw[i];
    if (!row) continue;
    let mLo = -1,
      tLe = -1;
    for (let j = 0; j < row.length; j++) {
      const v = trim(row[j]);
      if (v === "Mã lô") mLo = j;
      if (v === "Tỷ lệ ĐC") tLe = j;
    }
    if (mLo !== -1 && tLe !== -1) {
      hRow = i;
      maLoIdx = mLo;
      tyLeIdx = tLe;
      break;
    }
  }
  if (hRow === -1) return result;

  // ── Pass 2a: Detect cột đơn (unique) – quét theo THỨ TỰ ƯU TIÊN ─────────
  // ⚠️ VẤN ĐỀ GỐC (giải thích):
  //   Excel có cấu trúc header NHIỀU TẦNG. Ví dụ:
  //     hRow-2: "THÔNG TIN" (group merged) | "NHÂN VIÊN" | "GĐK" | ...
  //     hRow-1: "Hệ số K" | "NCC" | "Giá tính PDV" | "Tỷ lệ TN" | ...  ← sub-header
  //     hRow:   "Mã lô"   | "Tỷ lệ ĐC" | ...
  //   Ngoài ra, group-header ở tầng trên (hRow-2) đôi khi cũng có ô
  //   chứa đúng text "NCC" hay "Hệ số K" (vì merged cells chỉ giữ giá trị
  //   ở ô đầu tiên, và ô đó có thể trùng tên với header con phía dưới).
  //
  //   Fix cũ (hRow±2, quét từ scanStart đến scanEnd) dùng logic "first-wins":
  //   nếu "NCC" xuất hiện ở hRow-2 tại CỘT SAI trước, thì vị trí đúng ở
  //   hRow-1 sẽ bị bỏ qua → đọc dữ liệu ra 0.
  //
  //   Fix MỚI: quét hRow TRƯỚC (ưu tiên cao nhất), rồi mới mở rộng ra
  //   hRow±1, hRow±2, … Nếu label tìm thấy ngay ở hRow → đúng vị trí.
  //   Các dòng phụ chỉ dùng để fallback khi hRow không có label đó.
  // ───────────────────────────────────────────────────────────────────────────
  let colTransactionRate = -1; // "Tỷ lệ nhận về"
  let colIncomeRate = -1; // "Tỷ lệ TN" / "Tỷ lệ TN MSH"
  let colSalesRate = -1; // "Tỷ lệ PDV CVKD" (NOT Elite)
  let colEliteRate = -1; // "Tỷ lệ PDV CVKD Elite"
  let colKRate = -1; // "Hệ số dự án (K)" / "Hệ số K"
  let colNCC = -1; // "NCC"
  let colGiaTPDV = -1; // "Giá tính PDV"
  let colMaSale1 = -1; // "Mã Sale 1"
  let colMaSale2 = -1; // "Mã Sale 2"

  // Thứ tự ưu tiên: hRow (delta=0), rồi hRow±1, hRow±2, ... tối đa ±6
  const singleScanOrder = [0, -1, 1, -2, 2, -3, 3, -4, 4, -5, 5, -6, 6]
    .map((d) => hRow + d)
    .filter((i) => i >= 0 && i < raw.length);

  for (const i of singleScanOrder) {
    // Dừng sớm khi đã tìm đủ tất cả cột đơn
    if (
      colKRate !== -1 &&
      colNCC !== -1 &&
      colGiaTPDV !== -1 &&
      colTransactionRate !== -1 &&
      colIncomeRate !== -1 &&
      colSalesRate !== -1 &&
      colEliteRate !== -1 &&
      colMaSale1 !== -1 &&
      colMaSale2 !== -1
    )
      break;

    const row = raw[i];
    if (!row) continue;
    for (let j = 0; j < row.length; j++) {
      const v = trim(row[j]);
      if (!v) continue;

      // Thông tin chung – exact matching (trim() đã NFC)
      if (
        colKRate === -1 &&
        (v === "Hệ số dự án (K)" || v === "Hệ số dự án" || v === "Hệ số K")
      )
        colKRate = j;
      if (colNCC === -1 && v === "NCC") colNCC = j;
      if (colGiaTPDV === -1 && v === "Giá tính PDV") colGiaTPDV = j;
      if (colTransactionRate === -1 && v === "Tỷ lệ nhận về")
        colTransactionRate = j;
      if (colIncomeRate === -1 && (v === "Tỷ lệ TN" || v === "Tỷ lệ TN MSH"))
        colIncomeRate = j;

      // Nhân viên bán
      if (colMaSale1 === -1 && v === "Mã Sale 1") colMaSale1 = j;
      if (colMaSale2 === -1 && v === "Mã Sale 2") colMaSale2 = j;
      if (colSalesRate === -1 && v === "Tỷ lệ PDV CVKD") colSalesRate = j;
      if (colEliteRate === -1 && v === "Tỷ lệ PDV CVKD Elite") colEliteRate = j;
    }
  }

  // ── Pass 2b: Detect cột đa xuất hiện (multi-occurrence) – quét hRow±2 ──
  // Các cột NC, Mã, Tỷ lệ PDV, Số tiền xuất hiện nhiều lần → dùng Set.
  // Quét hRow±2 theo thứ tự tuần tự, thứ tự không ảnh hưởng kết quả (Set).
  const tyLePDVColSet = new Set();
  const soTienColSet = new Set();
  const ncColSet = new Set();
  const maColSet = new Set();
  const soTienTNColSet = new Set();
  const soTienPDVCVKDColSet = new Set();
  const soTienPDVCVKDEliteColSet = new Set();

  const multiScanStart = Math.max(0, hRow - 2);
  const multiScanEnd = Math.min(raw.length, hRow + 3);
  for (let i = multiScanStart; i < multiScanEnd; i++) {
    const row = raw[i];
    if (!row) continue;
    for (let j = 0; j < row.length; j++) {
      const v = trim(row[j]);
      if (!v) continue;
      if (v === "Tỷ lệ PDV") tyLePDVColSet.add(j);
      if (v === "Số tiền") soTienColSet.add(j);
      if (v === "NC") ncColSet.add(j);
      if (v === "Mã") maColSet.add(j);
      if (v === "Số tiền TN") soTienTNColSet.add(j);
      if (v === "Số tiền PDV CVKD") soTienPDVCVKDColSet.add(j);
      if (v === "Số tiền PDV CVKD Elite") soTienPDVCVKDEliteColSet.add(j);
    }
  }

  // ── 2. Sort multi-occurrence col sets ──────────────────────────────────────
  const tyLePDVCols = [...tyLePDVColSet].sort((a, b) => a - b);
  const soTienCols = [...soTienColSet].sort((a, b) => a - b);
  const ncCols = [...ncColSet].sort((a, b) => a - b);
  const maCols = [...maColSet].sort((a, b) => a - b);
  const soTienTNCols = [...soTienTNColSet].sort((a, b) => a - b);
  const soTienPDVCVKDCols = [...soTienPDVCVKDColSet].sort((a, b) => a - b);
  const soTienPDVCVKDEliteCols = [...soTienPDVCVKDEliteColSet].sort(
    (a, b) => a - b,
  );

  // ── 3. Map NC/Mã/Số tiền columns → vai trò nhân viên ──────────────────────
  // NC: [0]=sale1, [1]=sale2(CTV), [2]=GDKDH, [3]=GDKDT, [4]=GDKCL,
  //     [5]=GDVDH, [6]=GDVDT, [7]=GDVCL, [8]=Referral
  // Mã (plain): [0]=GDKDH, [1]=GDKDT, [2]=GDKCL, [3]=GDVDH, [4]=GDVDT, [5]=GDVCL, [6]=Referral, [7]=Manager
  // Tỷ lệ PDV: [3]=Tổng GĐK, [7]=Tổng GĐV, [9]=Tổng cộng
  // "Số tiền" (plain) - dùng positional logic để tìm đúng:
  const getNextSoTien = (afterCol, beforeCol = Infinity) =>
    soTienCols.find((c) => c > afterCol && c < beforeCol) ?? -1;

  // Tổng GĐK: tyLePDVCols[3] + price ngay sau
  const colGDKTotalRate = tyLePDVCols[3] ?? -1;
  const colGDKTotalPrice =
    colGDKTotalRate !== -1
      ? getNextSoTien(colGDKTotalRate, tyLePDVCols[4] ?? Infinity)
      : -1;

  // Tổng GĐV: tyLePDVCols[7] + price ngay sau
  const colGDVTotalRate = tyLePDVCols[7] ?? -1;
  const colGDVTotalPrice =
    colGDVTotalRate !== -1
      ? getNextSoTien(colGDVTotalRate, tyLePDVCols[8] ?? Infinity)
      : -1;

  // Tổng cộng PDV
  const colTotalCommissionRate = tyLePDVCols[9] ?? -1;
  const colTotalCommissionPrice =
    colTotalCommissionRate !== -1
      ? (soTienCols.find((c) => c > colTotalCommissionRate) ??
        colTotalCommissionRate + 1)
      : -1;

  // Per-person GĐK/GĐV prices (Số tiền plain, giữa NC và Mã tiếp theo)
  const getPerPersonPrice = (ncIdx, nextMaIdx) =>
    ncIdx !== -1 ? getNextSoTien(ncIdx, nextMaIdx ?? Infinity) : -1;

  const priceGDKDH = getPerPersonPrice(ncCols[2], maCols[1]);
  const priceGDKDT = getPerPersonPrice(ncCols[3], maCols[2]);
  const priceGDKCL = getPerPersonPrice(ncCols[4], maCols[3]);
  const priceGDVDH = getPerPersonPrice(ncCols[5], maCols[4]);
  const priceGDVDT = getPerPersonPrice(ncCols[6], maCols[5]);
  const priceGDVCL = getPerPersonPrice(ncCols[7], maCols[6]);
  const priceReferral = getPerPersonPrice(ncCols[8], maCols[7]);

  // ── 4. Tất cả mã nhân viên để build lookup maps ───────────────────────────
  const allEmpCols = [colMaSale1, colMaSale2, ...maCols].filter(
    (c) => c !== -1,
  );

  // ── 5. Duyệt data rows – TÍCH LŨY (không dedup) ──────────────────────────
  // Map: "maCan__tyLe" → accumulated mainTabRow entry
  const mainTabMap = new Map();

  for (let i = hRow + 1; i < raw.length; i++) {
    const row = raw[i];
    if (!row) continue;
    const maCan = trim(row[maLoIdx]);
    const tyLe = normPercent(row[tyLeIdx]);

    if (!maCan || isSkipRow(maCan) || tyLe <= 0) continue;

    // ── Lookup maps (backward compat) ──
    if (!result.maCanToTyLe.has(maCan)) result.maCanToTyLe.set(maCan, []);
    const existing = result.maCanToTyLe.get(maCan);
    if (!existing.includes(tyLe)) existing.push(tyLe);

    for (const idx of allEmpCols) {
      const empCode = trim(row[idx] ?? "");
      if (empCode && !isSkipRow(empCode)) {
        const key = `${maCan}__${empCode}`;
        if (!result.empMaCanToTyLe.has(key)) {
          result.empMaCanToTyLe.set(key, tyLe);
        }
      }
    }

    // ── Accumulate entry ─────────────────────────────────────────────────────
    const dedupKey = `${maCan}__${tyLe}`;
    const isFirstRow = !mainTabMap.has(dedupKey);

    if (isFirstRow) {
      // Khởi tạo entry với thông tin chung (chỉ lấy từ dòng đầu)
      mainTabMap.set(dedupKey, {
        codeApartment: maCan,
        calculateCommissionRate: tyLe,
        // Thông tin chung
        kRate:
          colKRate !== -1 && row[colKRate] != null
            ? toNum(row[colKRate])
            : null,
        priceForCommission:
          colGiaTPDV !== -1 && row[colGiaTPDV] != null
            ? toNum(row[colGiaTPDV])
            : null,
        standardWorkingDaysInMonth:
          colNCC !== -1 && row[colNCC] != null ? toNum(row[colNCC]) : null,
        transactionRate:
          colTransactionRate !== -1 && row[colTransactionRate] != null
            ? normPercent(row[colTransactionRate])
            : null,
        incomeRate:
          colIncomeRate !== -1 && row[colIncomeRate] != null
            ? normPercent(row[colIncomeRate])
            : null,
        salesCommissionRate:
          colSalesRate !== -1 && row[colSalesRate] != null
            ? normPercent(row[colSalesRate])
            : null,
        eliteSalesCommissionRate:
          colEliteRate !== -1 && row[colEliteRate] != null
            ? normPercent(row[colEliteRate])
            : null,
        // Aggregate totals – khởi tạo null, sẽ được cộng dồn từng dòng bên dưới
        // (hỗ trợ trường hợp 1 căn có 2 dòng Excel khi có 2 sale chính)
        gdkTotalRate: null,
        gdkTotalPrice: null,
        gdvTotalRate: null,
        gdvTotalPrice: null,
        totalCommissionRate: null,
        totalCommissionPrice: null,
        // Per-employee arrays
        employees: [], // sale1/ctv1/sale2/ctv2
        gdkEmployees: [], // GDKDH/DT/CL
        gdvEmployees: [], // GDVDH/DT/CL
        referralEmployees: [], // Người GTDL
        // Running totals
        _totalTNSales: 0,
        _totalPDVSales: 0,
        _rowCount: 0,
      });
    }

    const entry = mainTabMap.get(dedupKey);
    const rowIndex = entry._rowCount; // 0 = dòng 1, 1 = dòng 2
    entry._rowCount++;

    // ── Fallback: cập nhật thông tin chung nếu chưa đọc được (merged cells) ──
    if (entry.kRate == null && colKRate !== -1 && row[colKRate] != null)
      entry.kRate = toNum(row[colKRate]);
    if (
      entry.priceForCommission == null &&
      colGiaTPDV !== -1 &&
      row[colGiaTPDV] != null
    )
      entry.priceForCommission = toNum(row[colGiaTPDV]);
    if (
      entry.standardWorkingDaysInMonth == null &&
      colNCC !== -1 &&
      row[colNCC] != null
    )
      entry.standardWorkingDaysInMonth = toNum(row[colNCC]);
    if (
      entry.transactionRate == null &&
      colTransactionRate !== -1 &&
      row[colTransactionRate] != null
    )
      entry.transactionRate = normPercent(row[colTransactionRate]);
    if (
      entry.incomeRate == null &&
      colIncomeRate !== -1 &&
      row[colIncomeRate] != null
    )
      entry.incomeRate = normPercent(row[colIncomeRate]);
    if (
      entry.salesCommissionRate == null &&
      colSalesRate !== -1 &&
      row[colSalesRate] != null
    )
      entry.salesCommissionRate = normPercent(row[colSalesRate]);
    if (
      entry.eliteSalesCommissionRate == null &&
      colEliteRate !== -1 &&
      row[colEliteRate] != null
    )
      entry.eliteSalesCommissionRate = normPercent(row[colEliteRate]);

    // ── Cộng dồn aggregate totals (xử lý đúng trường hợp 2 dòng cùng căn/tyLe) ──
    // GĐK/GĐV rate & price: mỗi dòng mang phần của 1 sale chính → cộng lại để ra tổng toàn căn
    if (colGDKTotalRate !== -1 && row[colGDKTotalRate] != null)
      entry.gdkTotalRate =
        (entry.gdkTotalRate ?? 0) + normPercent(row[colGDKTotalRate]);
    if (colGDKTotalPrice !== -1 && row[colGDKTotalPrice] != null)
      entry.gdkTotalPrice =
        (entry.gdkTotalPrice ?? 0) + toNum(row[colGDKTotalPrice]);
    if (colGDVTotalRate !== -1 && row[colGDVTotalRate] != null)
      entry.gdvTotalRate =
        (entry.gdvTotalRate ?? 0) + normPercent(row[colGDVTotalRate]);
    if (colGDVTotalPrice !== -1 && row[colGDVTotalPrice] != null)
      entry.gdvTotalPrice =
        (entry.gdvTotalPrice ?? 0) + toNum(row[colGDVTotalPrice]);
    // totalCommissionPrice: cộng dồn vì mỗi dòng chứa tiền của 1 sale chính
    if (colTotalCommissionPrice !== -1 && row[colTotalCommissionPrice] != null)
      entry.totalCommissionPrice =
        (entry.totalCommissionPrice ?? 0) + toNum(row[colTotalCommissionPrice]);
    // totalCommissionRate: cộng dồn raw (sẽ được hiệu chỉnh trong finalize).
    // Lý do: mỗi dòng Excel = part1 (incomeRate+salesRate...) + GĐK/GĐV/TGĐ phần của sale đó.
    // Cộng dồn N dòng → part1 bị nhân N lần → cần trừ đi (N-1)×part1 khi finalize.
    if (colTotalCommissionRate !== -1 && row[colTotalCommissionRate] != null)
      entry.totalCommissionRate =
        (entry.totalCommissionRate ?? 0) +
        normPercent(row[colTotalCommissionRate]);

    // ── Xác định vai trò nhân viên bán theo rowIndex ──
    // Dòng 0: sale1 = Mã Sale 1, ctv1 = Mã Sale 2
    // Dòng 1: sale2 = Mã Sale 1, ctv2 = Mã Sale 2
    const sale1Role = rowIndex === 0 ? "sale1" : "sale2";
    const ctv1Role = rowIndex === 0 ? "ctv1" : "ctv2";

    // Extract sale1
    const s1Code = colMaSale1 !== -1 ? trim(row[colMaSale1] ?? "") : "";
    const s1NC =
      ncCols[0] !== undefined && row[ncCols[0]] != null
        ? toNum(row[ncCols[0]])
        : null;
    const s1TN =
      soTienTNCols[0] !== undefined && row[soTienTNCols[0]] != null
        ? toNum(row[soTienTNCols[0]])
        : null;
    const s1PDVCVKD =
      soTienPDVCVKDCols[0] !== undefined && row[soTienPDVCVKDCols[0]] != null
        ? toNum(row[soTienPDVCVKDCols[0]])
        : null;
    const s1PDVElite =
      soTienPDVCVKDEliteCols[0] !== undefined &&
      row[soTienPDVCVKDEliteCols[0]] != null
        ? toNum(row[soTienPDVCVKDEliteCols[0]])
        : null;

    if (
      s1Code &&
      !isSkipRow(s1Code) &&
      !entry.employees.find((e) => e.codeEmployee === s1Code)
    ) {
      entry.employees.push({
        codeEmployee: s1Code,
        role: sale1Role,
        workingDays: s1NC,
        salesCommissionPrice: s1PDVCVKD,
        eliteSalesCommissionPrice: s1PDVElite,
      });
      entry._totalTNSales += s1TN ?? 0;
      entry._totalPDVSales += (s1PDVCVKD ?? 0) + (s1PDVElite ?? 0);
    }

    // Extract sale2 (CTV)
    const s2Code = colMaSale2 !== -1 ? trim(row[colMaSale2] ?? "") : "";
    const s2NC =
      ncCols[1] !== undefined && row[ncCols[1]] != null
        ? toNum(row[ncCols[1]])
        : null;
    const s2TN =
      soTienTNCols[1] !== undefined && row[soTienTNCols[1]] != null
        ? toNum(row[soTienTNCols[1]])
        : null;
    const s2PDVCVKD =
      soTienPDVCVKDCols[1] !== undefined && row[soTienPDVCVKDCols[1]] != null
        ? toNum(row[soTienPDVCVKDCols[1]])
        : null;
    const s2PDVElite =
      soTienPDVCVKDEliteCols[1] !== undefined &&
      row[soTienPDVCVKDEliteCols[1]] != null
        ? toNum(row[soTienPDVCVKDEliteCols[1]])
        : null;

    if (
      s2Code &&
      !isSkipRow(s2Code) &&
      !entry.employees.find((e) => e.codeEmployee === s2Code)
    ) {
      entry.employees.push({
        codeEmployee: s2Code,
        role: ctv1Role,
        workingDays: s2NC,
        salesCommissionPrice: s2PDVCVKD,
        eliteSalesCommissionPrice: s2PDVElite,
      });
      entry._totalTNSales += s2TN ?? 0;
      entry._totalPDVSales += (s2PDVCVKD ?? 0) + (s2PDVElite ?? 0);
    }

    // ── GĐK employees ──
    // Dedup theo (code + role) vì 1 người có thể đồng thời là GDKDH, GDKDT, GDKCL.
    // Khi có 2 dòng cùng căn (2 sale chính), cùng GĐK xuất hiện ở cả 2 dòng →
    // cộng dồn commissionPrice thay vì bỏ qua dòng thứ 2.
    const addGDKEmp = (maIdx, ncIdx, priceCol, role) => {
      const code =
        maCols[maIdx] !== undefined ? trim(row[maCols[maIdx]] ?? "") : "";
      if (!code || isSkipRow(code)) return;
      const existingGDK = entry.gdkEmployees.find(
        (e) => e.codeEmployee === code && e.role === role,
      );
      if (existingGDK) {
        if (priceCol !== -1 && row[priceCol] != null)
          existingGDK.commissionPrice =
            (existingGDK.commissionPrice ?? 0) + toNum(row[priceCol]);
        return;
      }
      entry.gdkEmployees.push({
        codeEmployee: code,
        role,
        workingDays:
          ncIdx !== undefined && row[ncIdx] != null ? toNum(row[ncIdx]) : null,
        commissionPrice:
          priceCol !== -1 && row[priceCol] != null
            ? toNum(row[priceCol])
            : null,
      });
    };
    addGDKEmp(0, ncCols[2], priceGDKDH, "gdkdh");
    addGDKEmp(1, ncCols[3], priceGDKDT, "gdkdt");
    addGDKEmp(2, ncCols[4], priceGDKCL, "gdkcl");

    // ── GĐV employees ──
    // Dedup theo (code + role) vì 1 người có thể đồng thời là GDVDH, GDVDT, GDVCL.
    // Khi có 2 dòng cùng căn (2 sale chính), cộng dồn commissionPrice dòng thứ 2.
    const addGDVEmp = (maIdx, ncIdx, priceCol, role) => {
      const code =
        maCols[maIdx] !== undefined ? trim(row[maCols[maIdx]] ?? "") : "";
      if (!code || isSkipRow(code)) return;
      const existingGDV = entry.gdvEmployees.find(
        (e) => e.codeEmployee === code && e.role === role,
      );
      if (existingGDV) {
        if (priceCol !== -1 && row[priceCol] != null)
          existingGDV.commissionPrice =
            (existingGDV.commissionPrice ?? 0) + toNum(row[priceCol]);
        return;
      }
      entry.gdvEmployees.push({
        codeEmployee: code,
        role,
        workingDays:
          ncIdx !== undefined && row[ncIdx] != null ? toNum(row[ncIdx]) : null,
        commissionPrice:
          priceCol !== -1 && row[priceCol] != null
            ? toNum(row[priceCol])
            : null,
      });
    };
    addGDVEmp(3, ncCols[5], priceGDVDH, "gdvdh");
    addGDVEmp(4, ncCols[6], priceGDVDT, "gdvdt");
    addGDVEmp(5, ncCols[7], priceGDVCL, "gdvcl");

    // ── Referral (Người GTDL) ──
    // Khi có 2 dòng cùng căn, cùng GTDL xuất hiện ở cả 2 → cộng dồn commissionPrice.
    const refCode = maCols[6] !== undefined ? trim(row[maCols[6]] ?? "") : "";
    if (refCode && !isSkipRow(refCode)) {
      const existingRef = entry.referralEmployees.find(
        (e) => e.codeEmployee === refCode,
      );
      if (existingRef) {
        if (priceReferral !== -1 && row[priceReferral] != null)
          existingRef.commissionPrice =
            (existingRef.commissionPrice ?? 0) + toNum(row[priceReferral]);
      } else {
        entry.referralEmployees.push({
          codeEmployee: refCode,
          role: "referral",
          workingDays:
            ncCols[8] !== undefined && row[ncCols[8]] != null
              ? toNum(row[ncCols[8]])
              : null,
          commissionPrice:
            priceReferral !== -1 && row[priceReferral] != null
              ? toNum(row[priceReferral])
              : null,
        });
      }
    }

    // ── Update totalTN/PDV tích lũy vào aggregate columns nếu có ──
    // (aggregate columns từ Excel là trên TỪNG dòng – ta dùng dòng cuối để lấy giá trị total)
    // Nếu có cột "Tổng TN"/"Tổng PDV" riêng, update vào entry ở mỗi dòng
  }

  // ── 6. Finalize: gắn totalTNSales/PDVSales vào mỗi entry ─────────────────
  for (const entry of mainTabMap.values()) {
    if (entry._totalTNSales > 0 || entry.employees.length > 0)
      entry.totalTNSales = entry._totalTNSales;
    if (entry._totalPDVSales > 0 || entry.employees.length > 0)
      entry.totalPDVSales = entry._totalPDVSales;
    // Hiệu chỉnh totalCommissionRate cho căn có nhiều dòng (nhiều sale chính):
    // Mỗi dòng Excel = part1 (incomeRate+salesRate+...) + phần GĐK/GĐV/TGĐ của sale đó.
    // Cộng dồn N dòng → part1 bị tính N lần. DB chỉ tính 1 lần. Trừ đi (N-1)×part1.
    if (entry._rowCount > 1 && entry.totalCommissionRate != null) {
      const part1 =
        (entry.incomeRate ?? 0) +
        (entry.platformIncomeRate ?? 0) +
        (entry.salesCommissionRate ?? 0) +
        (entry.eliteSalesCommissionRate ?? 0);
      entry.totalCommissionRate =
        Math.round(
          (entry.totalCommissionRate - (entry._rowCount - 1) * part1) * 10000,
        ) / 10000;
    }
    // Xóa internal fields
    delete entry._totalTNSales;
    delete entry._totalPDVSales;
    delete entry._rowCount;
    result.mainTabRows.push(entry);
  }

  return result;
};

// ─── Parse sheet "PDV theo căn" ───────────────────────────────────────────────
/**
 * Trả về mảng các row với các trường:
 *  codeEmployee, fullName, codeApartment, totalPDV,
 *  certificateOrDeposit, accounting, remainingAfterAccounting,
 *  personalIncomeTax, remainingAfterTax
 */
const parsePDVTheoCanTab = (wb, sheetName) => {
  const ws = wb.Sheets[sheetName];
  if (!ws) return { error: `Không tìm thấy sheet "${sheetName}"`, rows: [] };

  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });

  let colMNV = -1,
    colMaCan = -1,
    colTongPDV = -1,
    colChungChi = -1;
  let colKT = -1,
    colSauKT = -1,
    colThue = -1,
    colSauThue = -1;
  let colNguoiDungTenMa = -1; // Mã cột "Người đứng tên" (ưu tiên hơn cột Nhân sự MNV)
  let hdrRowIdx = -1; // Dòng header chứa "MNV"
  let dataStartRow = -1;

  for (let i = 0; i < Math.min(raw.length, 20); i++) {
    const row = raw[i];
    if (!row) continue;
    for (let j = 0; j < row.length; j++) {
      const v = trim(row[j]);
      if (v === "MNV" && colMNV === -1) {
        colMNV = j;
        hdrRowIdx = i;
      }
      if (v === "Mã căn" && colMaCan === -1) colMaCan = j;
      if (v === "Tổng cộng PDV" && colTongPDV === -1) colTongPDV = j;
      if (v === "Chứng chỉ/ ký quỹ" && colChungChi === -1) colChungChi = j;
      if (
        colKT === -1 &&
        (v.includes("tạm ứng") || (v.includes("KT") && v.includes("ĐC")))
      )
        colKT = j;
      if (v === "PDV còn lại sau KT" && colSauKT === -1) colSauKT = j;
      if (v === "Thuế TNCN" && colThue === -1) colThue = j;
      if (v === "PDV còn lại sau thuế" && colSauThue === -1) colSauThue = j;
    }
    if (colMNV !== -1 && colTongPDV !== -1 && dataStartRow === -1) {
      dataStartRow = i + 1;
    }
  }

  // Detect cột "Người đứng tên – Mã":
  // Trong header row chứa "MNV", tìm cột "Mã" đầu tiên xuất hiện SAU colMNV
  // (cấu trúc chuẩn: MNV | Họ tên NV | Mã NDT | Họ tên NDT | Mã căn | ...)
  if (hdrRowIdx !== -1 && colMNV !== -1) {
    const hdrRow = raw[hdrRowIdx] || [];
    for (let j = colMNV + 1; j < hdrRow.length; j++) {
      const v = trim(hdrRow[j]);
      if (v === "Mã căn") break; // dừng trước cột Mã căn
      if (v === "Mã") {
        colNguoiDungTenMa = j;
        break;
      }
    }
  }

  if (colMNV === -1)
    return {
      error: 'Không tìm thấy cột "MNV" trong sheet PDV theo căn',
      rows: [],
    };
  if (colTongPDV === -1)
    return {
      error: 'Không tìm thấy cột "Tổng cộng PDV" trong sheet PDV theo căn',
      rows: [],
    };

  // Mã căn thường nằm ở MNV+4 (MNV | Họ tên | Mã NDT | Họ tên NDT | Mã căn)
  if (colMaCan === -1) colMaCan = colMNV + 4;

  // Ưu tiên cột "Người đứng tên – Mã" (chính xác hơn khi NV và NDT khác nhau),
  // fallback về cột "Nhân sự – MNV" nếu không detect được.
  const colEmpCode = colNguoiDungTenMa !== -1 ? colNguoiDungTenMa : colMNV;
  const colEmpName =
    colNguoiDungTenMa !== -1 ? colNguoiDungTenMa + 1 : colMNV + 1;

  const rows = [];
  for (let i = dataStartRow; i < raw.length; i++) {
    const row = raw[i];
    if (!row) continue;
    const mnv = trim(row[colMNV]); // vẫn dùng MNV để nhận biết dòng dữ liệu hợp lệ
    const maCan = trim(row[colMaCan]);

    if (!mnv || !maCan) continue;
    if (isSkipRow(mnv) || isSkipRow(maCan)) continue;

    const codeEmployee = trim(row[colEmpCode]);
    const fullName = trim(row[colEmpName]);

    // Bỏ qua nếu mã Người đứng tên trống/không hợp lệ
    if (!codeEmployee || isSkipRow(codeEmployee)) continue;

    rows.push({
      codeEmployee,
      fullName,
      codeApartment: maCan,
      totalPDV: toNum(row[colTongPDV]),
      certificateOrDeposit: colChungChi !== -1 ? toNum(row[colChungChi]) : 0,
      accounting: colKT !== -1 ? toNum(row[colKT]) : 0,
      remainingAfterAccounting: colSauKT !== -1 ? toNum(row[colSauKT]) : 0,
      personalIncomeTax: colThue !== -1 ? toNum(row[colThue]) : 0,
      remainingAfterTax: colSauThue !== -1 ? toNum(row[colSauThue]) : 0,
    });
  }

  return { error: null, rows };
};

// ─── Join 2 tab để lấy calculateCommissionRate cho theoCanRows ────────────────
/**
 * Trả về:
 *  theoCanInput : row[] đã có calculateCommissionRate từ sheet chính
 *  mainTabRows  : đã build sẵn trong parsePDVMainTab (pass-through)
 *  warnings     : cảnh báo khi không ghép được
 */
const buildInput = (mainTabData, theoCanRows) => {
  const { maCanToTyLe, empMaCanToTyLe, mainTabRows } = mainTabData;
  const theoCanInput = [];
  const warnings = [];

  for (const row of theoCanRows) {
    const { codeApartment, codeEmployee } = row;
    // Ưu tiên khóa kép (chính xác hơn)
    const keyDouble = `${codeApartment}__${codeEmployee}`;
    let calculateCommissionRate = empMaCanToTyLe.get(keyDouble);

    if (calculateCommissionRate == null) {
      // Fallback: dùng maCan → [tyLe]
      const tyLeList = maCanToTyLe.get(codeApartment);
      if (!tyLeList || tyLeList.length === 0) {
        warnings.push(
          `Không tìm thấy Tỷ lệ ĐC cho căn "${codeApartment}" (NV: ${codeEmployee})`,
        );
        continue;
      }
      if (tyLeList.length === 1) {
        calculateCommissionRate = tyLeList[0];
      } else {
        warnings.push(
          `Căn "${codeApartment}" có ${tyLeList.length} Tỷ lệ ĐC [${tyLeList.join(", ")}], dùng tất cả để so sánh`,
        );
        for (const tyLe of tyLeList) {
          theoCanInput.push({ ...row, calculateCommissionRate: tyLe });
        }
        continue;
      }
    }

    theoCanInput.push({ ...row, calculateCommissionRate });
  }

  return { theoCanInput, mainTabRows, warnings };
};

// ─── UI Components ────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    MATCH: { label: "✅ Khớp", colorScheme: "green" },
    MISMATCH: { label: "❌ Lệch", colorScheme: "red" },
    ERROR: { label: "⚠️ Lỗi", colorScheme: "orange" },
    SKIPPED: { label: "— Bỏ qua", colorScheme: "gray" },
  };
  const cfg = map[status] || { label: status, colorScheme: "gray" };
  return <Badge colorScheme={cfg.colorScheme}>{cfg.label}</Badge>;
}

function ComparisonTable({ comparisons, borderColor }) {
  return (
    <Table size="sm" variant="simple">
      <Thead>
        <Tr>
          <Th>Trường</Th>
          <Th isNumeric>Excel</Th>
          <Th isNumeric>DB (Hệ thống)</Th>
          <Th isNumeric>Chênh lệch</Th>
          <Th>Kết quả</Th>
        </Tr>
      </Thead>
      <Tbody>
        {comparisons.map((c, i) => (
          <Tr key={i}>
            <Td>{c.label}</Td>
            <Td isNumeric>{fmtValue(c.label, c.excel)}</Td>
            <Td isNumeric>{fmtValue(c.label, c.db)}</Td>
            <Td isNumeric color={c.diff !== 0 ? "red.500" : "green.600"}>
              {c.diff !== 0
                ? (c.diff > 0 ? "+" : "") +
                  (isRateLabel(c.label)
                    ? c.diff
                    : isPlainNumberLabel(c.label)
                      ? c.diff
                      : Math.round(c.diff).toLocaleString("vi-VN"))
                : "—"}
            </Td>
            <Td>{c.match ? "✅" : "❌"}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

const roleLabel = {
  sale1: "Sale chính 1",
  ctv1: "CTV 1",
  sale2: "Sale chính 2",
  ctv2: "CTV 2",
  gdkdh: "GĐK ĐH",
  gdkdt: "GĐK ĐT",
  gdkcl: "GĐK CL",
  gdvdh: "GĐV ĐH",
  gdvdt: "GĐV ĐT",
  gdvcl: "GĐV CL",
  referral: "Người GTDL",
};

function ResultRow({ row }) {
  const [open, setOpen] = useState(row.status === "MISMATCH");
  const borderColor = row.status === "MATCH" ? "green.300" : "red.300";
  const isMainTab = row.type === "mainTab";
  const hasDetail =
    (row.comparisons && row.comparisons.length > 0) ||
    (row.empComparisons && row.empComparisons.length > 0);

  return (
    <>
      <Tr
        bg={
          row.status === "MATCH"
            ? "green.50"
            : row.status === "MISMATCH"
              ? "red.50"
              : row.status === "SKIPPED"
                ? "gray.50"
                : "orange.50"
        }
        cursor={hasDetail ? "pointer" : "default"}
        onClick={() => hasDetail && setOpen((o) => !o)}
      >
        <Td fontSize="xs" color="gray.500">
          {isMainTab ? "📊 Tổng/NV" : "👤 PDV theo căn"}
        </Td>
        <Td fontWeight="600">{row.codeApartment}</Td>
        <Td>
          {row.calculateCommissionRate != null
            ? `${row.calculateCommissionRate}%`
            : "-"}
        </Td>
        <Td>{isMainTab ? "-" : row.codeEmployee}</Td>
        <Td>{isMainTab ? "-" : row.fullName || "-"}</Td>
        <Td>{row.period ?? "-"}</Td>
        <Td>
          <StatusBadge status={row.status} />
        </Td>
        <Td color="gray.500" fontSize="sm" maxW="260px" whiteSpace="normal">
          {row.error || (hasDetail ? "Click để xem chi tiết ↓" : "")}
        </Td>
        <Td>
          {hasDetail && (
            <IconButton
              size="xs"
              icon={open ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
              aria-label="toggle"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((o) => !o);
              }}
            />
          )}
        </Td>
      </Tr>

      {open && (
        <Tr>
          <Td colSpan={9} p={0}>
            <Box
              bg="white"
              borderLeft="4px solid"
              borderColor={borderColor}
              pl={4}
              py={3}
              pr={4}
            >
              {/* ── Tổng/Thông tin chung ── */}
              {row.comparisons && row.comparisons.length > 0 && (
                <Box mb={row.empComparisons?.length > 0 ? 4 : 0}>
                  <Text
                    fontSize="12px"
                    fontWeight="700"
                    color="gray.600"
                    mb={2}
                  >
                    📊 Thông tin chung & tổng PDV
                  </Text>
                  <ComparisonTable
                    comparisons={row.comparisons}
                    borderColor={borderColor}
                  />
                </Box>
              )}

              {/* ── Per-employee so sánh (chỉ cho mainTab) ── */}
              {row.empComparisons && row.empComparisons.length > 0 && (
                <Box>
                  <Text
                    fontSize="12px"
                    fontWeight="700"
                    color="gray.600"
                    mb={2}
                  >
                    👤 Chi tiết nhân viên (NC, Số tiền PDV CVKD, Elite)
                  </Text>
                  {row.empComparisons.map((emp, idx) => (
                    <Box
                      key={idx}
                      mb={3}
                      pl={2}
                      borderLeft="2px solid"
                      borderColor={
                        emp.status === "MATCH"
                          ? "green.200"
                          : emp.status === "MISMATCH"
                            ? "red.200"
                            : "orange.200"
                      }
                    >
                      <HStack mb={1}>
                        <Badge
                          colorScheme={
                            emp.status === "MATCH"
                              ? "green"
                              : emp.status === "MISMATCH"
                                ? "red"
                                : "orange"
                          }
                          fontSize="10px"
                        >
                          {emp.status === "MATCH"
                            ? "✅"
                            : emp.status === "MISMATCH"
                              ? "❌"
                              : "⚠️"}
                        </Badge>
                        <Text fontSize="12px" fontWeight="600">
                          {roleLabel[emp.role] || emp.role} – {emp.codeEmployee}
                          {emp.fullName ? ` (${emp.fullName})` : ""}
                        </Text>
                        {emp.error && (
                          <Text fontSize="11px" color="orange.600">
                            {emp.error}
                          </Text>
                        )}
                      </HStack>
                      {emp.comparisons && emp.comparisons.length > 0 && (
                        <ComparisonTable
                          comparisons={emp.comparisons}
                          borderColor={borderColor}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Td>
        </Tr>
      )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function CheckCommission() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [fileName, setFileName] = useState("");
  const [workbook, setWorkbook] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [detectedSheets, setDetectedSheets] = useState({
    main: "",
    theoCan: "",
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState(null);
  const [parseWarnings, setParseWarnings] = useState([]);
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterType, setFilterType] = useState("ALL"); // "ALL" | "mainTab" | "theoCan"
  const [filterApt, setFilterApt] = useState(""); // lọc theo mã căn/mã lô

  // ── Đọc file ────────────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "xlsx" && ext !== "xls") {
      toast.error("Chỉ hỗ trợ .xlsx / .xls");
      return;
    }

    setFileName(file.name);
    setResults(null);
    setSummary(null);
    setParseWarnings([]);
    setFilterStatus("ALL");
    setFilterType("ALL");
    setFilterApt("");

    const reader = new FileReader();
    reader.onload = (ev) => {
      const data = new Uint8Array(ev.target.result);
      const wb = XLSX.read(data, { type: "array" });
      setWorkbook(wb);
      setSheetNames(wb.SheetNames);

      // Auto-detect sheets
      const mainSheet = wb.SheetNames.find((n) => /PDV\s*\d{2}/i.test(n)) || "";
      const theoCanSheet =
        wb.SheetNames.find(
          (n) =>
            n.toLowerCase().includes("theo c") ||
            n.toLowerCase().includes("theo căn"),
        ) || "";
      setDetectedSheets({ main: mainSheet, theoCan: theoCanSheet });

      if (!mainSheet)
        toast.error('Không tìm thấy sheet "PDV tháng.năm" (vd: PDV 12.2025)');
      if (!theoCanSheet) toast.error('Không tìm thấy sheet "PDV theo căn"');
    };
    reader.readAsArrayBuffer(file);
  };

  // ── Kiểm tra ────────────────────────────────────────────────────────────────
  const handleCheck = async () => {
    if (!workbook) {
      toast.error("Vui lòng chọn file trước");
      return;
    }
    const { main, theoCan } = detectedSheets;
    if (!main || !theoCan) {
      toast.error(
        "Không xác định được 2 sheet cần thiết. Kiểm tra lại tên sheet.",
      );
      return;
    }

    // Trích tháng/năm từ tên sheet, vd: "PDV 12.2025" → month=12, year=2025
    const monthYearMatch = main.match(/(\d{1,2})[.\-/](\d{4})/);
    if (!monthYearMatch) {
      toast.error(
        'Không đọc được tháng/năm từ tên sheet. Tên sheet phải có dạng "PDV MM.YYYY" (vd: PDV 12.2025)',
      );
      return;
    }
    const month = parseInt(monthYearMatch[1], 10);
    const year = parseInt(monthYearMatch[2], 10);

    setLoading(true);
    setResults(null);
    setSummary(null);
    setParseWarnings([]);
    setFilterStatus("ALL");
    setFilterType("ALL");
    setFilterApt("");

    try {
      // ── Parse ──
      const mainTabData = parsePDVMainTab(workbook, main);
      const { error, rows: theoCanRows } = parsePDVTheoCanTab(
        workbook,
        theoCan,
      );

      if (error) {
        toast.error(error);
        return;
      }
      if (theoCanRows.length === 0) {
        toast.error("Sheet PDV theo căn không có dữ liệu hợp lệ");
        return;
      }

      // ── Join ──
      const { theoCanInput, mainTabRows, warnings } = buildInput(
        mainTabData,
        theoCanRows,
      );
      setParseWarnings(warnings);

      if (theoCanInput.length === 0) {
        toast.error(
          "Không ghép được dữ liệu PDV theo căn với Tỷ lệ ĐC từ sheet chính.",
        );
        return;
      }

      const totalRows = theoCanInput.length + mainTabRows.length;
      toast(
        `Đang so sánh ${totalRows} dòng với DB... (tháng ${month}/${year})`,
        { icon: "🔍" },
      );

      // ── Gọi BE – bỏ fullName để giảm payload (BE tự lấy từ Employee model) ──
      const theoCanPayload = theoCanInput.map(
        ({ fullName: _f, ...rest }) => rest, // eslint-disable-line no-unused-vars
      );

      // mainTabRows đã có đầy đủ: employees, gdkEmployees, gdvEmployees, referralEmployees,
      // totalTNSales, totalPDVSales, gdkTotalRate/Price, gdvTotalRate/Price, totalCommissionRate/Price,
      // kRate, priceForCommission, standardWorkingDaysInMonth, transactionRate, incomeRate
      const res = await commissionAnalystCall.checkPDVExcel({
        month,
        year,
        mainTabRows,
        theoCanRows: theoCanPayload,
      });

      const resData = res || {};
      const allResults = [
        ...(resData.mainTabResults || []),
        ...(resData.theoCanResults || []),
      ];
      setResults(allResults);
      setSummary(resData.summary || {});

      if (warnings.length > 0)
        toast(`⚠️ ${warnings.length} cảnh báo khi đọc file`, { icon: "⚠️" });
    } catch (_) {
      // toast đã hiện trong api
    } finally {
      setLoading(false);
    }
  };

  const filteredResults = results
    ? results.filter((r) => {
        if (filterStatus !== "ALL" && r.status !== filterStatus) return false;
        if (filterType !== "ALL" && r.type !== filterType) return false;
        if (
          filterApt.trim() &&
          !r.codeApartment
            ?.toLowerCase()
            .includes(filterApt.trim().toLowerCase())
        )
          return false;
        return true;
      })
    : [];

  return (
    <Box>
      {/* Back */}
      <Flex
        onClick={() => navigate("/service-fee-management")}
        cursor="pointer"
        gap="8px"
        alignItems="center"
        mb="24px"
        ml="24px"
        mt="24px"
        w="max-content"
      >
        <IoArrowBackOutline fontSize="24px" />
        <Text
          fontSize="20px"
          fontWeight="600"
          color="var(--color-header-employee)"
        >
          Quay lại
        </Text>
      </Flex>

      <Box px="24px">
        <Text
          fontSize="22px"
          fontWeight="700"
          mb="16px"
          color="var(--color-header-employee)"
        >
          Kiểm tra phí dịch vụ (PDV)
        </Text>

        {/* Upload */}
        <Box
          bg="white"
          borderRadius="12px"
          p="24px"
          mb="16px"
          border="1px solid"
          borderColor="gray.200"
          maxW="960px"
        >
          <Text fontWeight="600" mb="12px">
            1. Chọn file Excel "Bảng tách PDV tháng X.XXXX"
          </Text>
          <HStack spacing="12px" mb="16px">
            <Input
              value={fileName}
              isReadOnly
              placeholder="Chưa chọn file"
              bg="gray.50"
              flex="1"
            />
            <Button
              as="label"
              htmlFor="pdv-file-upload"
              colorScheme="blue"
              cursor="pointer"
              px="20px"
            >
              Chọn file
            </Button>
            <Input
              id="pdv-file-upload"
              type="file"
              accept=".xlsx,.xls"
              display="none"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </HStack>

          {/* Sheet detect status */}
          {sheetNames.length > 0 && (
            <Box bg="gray.50" borderRadius="8px" p="12px" mb="16px">
              <Text fontSize="13px" fontWeight="600" mb="6px">
                Sheets được tự động nhận diện:
              </Text>
              <HStack spacing="12px" flexWrap="wrap">
                <Badge colorScheme={detectedSheets.main ? "green" : "red"}>
                  {detectedSheets.main
                    ? `✓ ${detectedSheets.main}`
                    : "✗ Chưa tìm thấy sheet PDV chính"}
                </Badge>
                <Badge colorScheme={detectedSheets.theoCan ? "green" : "red"}>
                  {detectedSheets.theoCan
                    ? `✓ ${detectedSheets.theoCan}`
                    : "✗ Chưa tìm thấy PDV theo căn"}
                </Badge>
              </HStack>

              {/* Manual override */}
              <Flex gap="12px" mt="10px" flexWrap="wrap">
                <Box>
                  <Text fontSize="12px" color="gray.500" mb="2px">
                    Sheet PDV chính:
                  </Text>
                  <Select
                    size="sm"
                    value={detectedSheets.main}
                    maxW="200px"
                    onChange={(e) =>
                      setDetectedSheets((p) => ({ ...p, main: e.target.value }))
                    }
                  >
                    <option value="">-- Chọn sheet --</option>
                    {sheetNames.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Text fontSize="12px" color="gray.500" mb="2px">
                    Sheet PDV theo căn:
                  </Text>
                  <Select
                    size="sm"
                    value={detectedSheets.theoCan}
                    maxW="200px"
                    onChange={(e) =>
                      setDetectedSheets((p) => ({
                        ...p,
                        theoCan: e.target.value,
                      }))
                    }
                  >
                    <option value="">-- Chọn sheet --</option>
                    {sheetNames.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Select>
                </Box>
              </Flex>
            </Box>
          )}

          <Button
            colorScheme="teal"
            onClick={handleCheck}
            isLoading={loading}
            loadingText="Đang kiểm tra..."
            isDisabled={
              !workbook || !detectedSheets.main || !detectedSheets.theoCan
            }
            px="32px"
            h="44px"
          >
            🔍 Kiểm tra PDV
          </Button>
        </Box>

        {/* Hướng dẫn */}
        <Box
          bg="blue.50"
          border="1px solid"
          borderColor="blue.200"
          borderRadius="8px"
          p="14px"
          mb="20px"
          maxW="960px"
        >
          <Text fontWeight="600" color="blue.700" fontSize="13px" mb="6px">
            📋 Yêu cầu cấu trúc file Excel:
          </Text>
          <Box fontSize="12px" color="blue.800" lineHeight="1.9">
            <Box>
              • Sheet <b>PDV MM.YYYY</b> (vd: <i>PDV 12.2025</i>): cần cột{" "}
              <b>"Mã lô"</b>, <b>"Tỷ lệ ĐC"</b>, <b>"Tỷ lệ nhận về"</b>,{" "}
              <b>"Tỷ lệ PDV CVKD"</b>, và các cột <b>"Tỷ lệ PDV"</b> (cho
              GĐK/GĐV/TGĐ)
            </Box>
            <Box>
              • Sheet <b>"PDV theo căn"</b>: cần cột <b>"MNV"</b>,{" "}
              <b>"Mã căn"</b> (hoặc vị trí MNV+4), <b>"Tổng cộng PDV"</b>,{" "}
              <b>"Chứng chỉ/ ký quỹ"</b>, cột KT, <b>"PDV còn lại sau KT"</b>,{" "}
              <b>"Thuế TNCN"</b>, <b>"PDV còn lại sau thuế"</b>
            </Box>
            <Box>
              • Tên sheet PDV chính phải chứa tháng/năm (vd: <b>PDV 12.2025</b>)
              để xác định đúng đợt trong DB
            </Box>
          </Box>
        </Box>

        {/* Parse warnings */}
        {parseWarnings.length > 0 && (
          <Box
            bg="orange.50"
            border="1px solid"
            borderColor="orange.200"
            borderRadius="8px"
            p="12px"
            mb="16px"
            maxW="960px"
          >
            <Text fontWeight="600" color="orange.700" fontSize="13px" mb="6px">
              ⚠️ Cảnh báo khi đọc file ({parseWarnings.length}):
            </Text>
            {parseWarnings.slice(0, 10).map((w, i) => (
              <Text key={i} fontSize="12px" color="orange.800">
                • {w}
              </Text>
            ))}
            {parseWarnings.length > 10 && (
              <Text fontSize="12px" color="orange.600">
                ... và {parseWarnings.length - 10} cảnh báo khác
              </Text>
            )}
          </Box>
        )}

        {loading && (
          <Flex alignItems="center" gap="12px" mt="16px">
            <Spinner />
            <Text>Đang so sánh với dữ liệu hệ thống...</Text>
          </Flex>
        )}

        {/* Results */}
        {summary && (
          <Box mb="16px">
            {/* Summary badges */}
            <HStack spacing="16px" mb="12px" flexWrap="wrap">
              <Text fontWeight="700" fontSize="16px">
                Kết quả{" "}
                {summary.month && summary.year
                  ? `tháng ${summary.month}/${summary.year}`
                  : ""}
                :
              </Text>
              <Badge colorScheme="gray" fontSize="13px" px="10px" py="4px">
                Tổng: {summary.total}
              </Badge>
              {[
                ["MATCH", "green", `✅ Khớp: ${summary.match}`],
                ["MISMATCH", "red", `❌ Lệch: ${summary.mismatch}`],
                ["ERROR", "orange", `⚠️ Lỗi: ${summary.error}`],
              ].map(([status, color, label]) => (
                <Badge
                  key={status}
                  colorScheme={color}
                  fontSize="13px"
                  px="10px"
                  py="4px"
                  cursor="pointer"
                  onClick={() =>
                    setFilterStatus((f) => (f === status ? "ALL" : status))
                  }
                  opacity={
                    filterStatus !== "ALL" && filterStatus !== status ? 0.4 : 1
                  }
                >
                  {label}
                </Badge>
              ))}
              {filterStatus !== "ALL" && (
                <Button
                  size="xs"
                  variant="link"
                  colorScheme="blue"
                  onClick={() => setFilterStatus("ALL")}
                >
                  Xem tất cả
                </Button>
              )}
            </HStack>

            {/* Type filter + Search by căn */}
            <Flex gap="16px" mb="12px" alignItems="center" flexWrap="wrap">
              <HStack spacing="8px">
                <Text fontSize="13px" color="gray.500">
                  Loại:
                </Text>
                {[
                  ["ALL", "gray", "Tất cả"],
                  ["mainTab", "purple", "📊 Tỷ lệ/Tổng"],
                  ["theoCan", "blue", "👤 Nhân viên"],
                ].map(([type, color, label]) => (
                  <Badge
                    key={type}
                    colorScheme={color}
                    fontSize="12px"
                    px="8px"
                    py="3px"
                    cursor="pointer"
                    onClick={() =>
                      setFilterType((f) => (f === type ? "ALL" : type))
                    }
                    opacity={
                      filterType !== "ALL" && filterType !== type ? 0.4 : 1
                    }
                  >
                    {label}
                  </Badge>
                ))}
              </HStack>
              <HStack spacing="6px">
                <Text fontSize="13px" color="gray.500" whiteSpace="nowrap">
                  Lọc mã căn:
                </Text>
                <Input
                  size="sm"
                  placeholder="VD: A1-01"
                  value={filterApt}
                  onChange={(e) => setFilterApt(e.target.value)}
                  maxW="160px"
                  borderRadius="6px"
                />
                {filterApt && (
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="gray"
                    onClick={() => setFilterApt("")}
                  >
                    ✕
                  </Button>
                )}
              </HStack>
            </Flex>

            <TableContainer
              border="1px solid"
              borderColor="gray.200"
              borderRadius="8px"
              maxH="70vh"
              overflowY="auto"
            >
              <Table variant="simple" size="sm">
                <Thead position="sticky" top={0} bg="gray.100" zIndex={1}>
                  <Tr>
                    <Th w="80px">Loại</Th>
                    <Th>Mã lô</Th>
                    <Th>Tỷ lệ ĐC</Th>
                    <Th>Mã NV</Th>
                    <Th>Họ tên</Th>
                    <Th>Đợt (DB)</Th>
                    <Th>Trạng thái</Th>
                    <Th>Ghi chú</Th>
                    <Th w="40px" />
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredResults.length === 0 ? (
                    <Tr>
                      <Td
                        colSpan={9}
                        textAlign="center"
                        color="gray.400"
                        py={8}
                      >
                        Không có kết quả
                      </Td>
                    </Tr>
                  ) : (
                    filteredResults.map((row, i) => (
                      <ResultRow key={i} row={row} />
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    </Box>
  );
}
